import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { Dialog, DialogBody, DialogFooter } from '@blueprintjs/core';
import { toast } from 'sonner';
import { csvText, downloadText, fetchExport, type DuplicateReport, type ExportFilter } from './api';
import { leadImportKeys } from './keys';
import { queryPolicies } from '../../utils/queryPolicies';
import { ConnectDriveBanner, DriveNotConnected, useOpenInSheets } from './FileChip';
import { ErrorNote, Segmented, fmtInt } from './ui';
import { PickList } from '../ui/PickList';

const PREVIEW_ROWS = 50;

const DUPLICATES: { value: ExportFilter['duplicates']; label: string; hint: string }[] = [
  { value: 'all', label: 'Everything', hint: 'Every lead of this import' },
  { value: 'only', label: 'Only duplicates', hint: 'Leads whose phone came in an earlier import' },
  { value: 'except', label: 'All except duplicates', hint: 'Leads never seen in an earlier import' },
];

/**
 * [spec v4 C2] The import's leads, filtered, previewed, then opened in Google
 * Sheets or downloaded as CSV. Readable only by lead import operators.
 */
export default function ExportDialog({ batchId, ownSource, report, reps, initial, onClose }: {
  batchId: number;
  /** The import's lead source, for duplicates from the same source. */
  ownSource: number | null;
  report: DuplicateReport | undefined;
  reps: { rep: number; name: string }[];
  initial?: Partial<ExportFilter>;
  onClose: () => void;
}) {
  const [filter, setFilter] = useState<ExportFilter>({ duplicates: 'all', source: null, rep: null, ...initial });
  const set = (patch: Partial<ExportFilter>) => setFilter((f) => ({ ...f, ...patch }));
  const preview = useQuery({
    queryKey: leadImportKeys.exportPreview(batchId, filter),
    queryFn: () => fetchExport(batchId, filter, PREVIEW_ROWS),
    placeholderData: keepPreviousData,
    ...queryPolicies.pagedData,
  });
  const total = preview.data?.total ?? 0;

  const sources = [
    ...(report && ownSource != null && report.same_source > 0 ? [{ id: ownSource, label: `This import's source (${fmtInt(report.same_source)})` }] : []),
    ...(report?.other_sources ?? []).filter((s) => s.lead_source != null).map((s) => ({ id: s.lead_source as number, label: `${s.source} (${fmtInt(s.leads)})` })),
  ];
  const name = `Import ${batchId} ${DUPLICATES.find((d) => d.value === filter.duplicates)?.label.toLowerCase()}.csv`;
  const fullCsv = async () => {
    const t = await fetchExport(batchId, filter, null);
    return csvText(t.columns, t.rows);
  };
  const download = useMutation({
    mutationFn: async () => downloadText(name, await fullCsv()),
    onError: (e) => toast.error((e as Error).message),
  });
  const sheets = useOpenInSheets(async () => ({ name, blob: new Blob([await fullCsv()], { type: 'text/csv' }) }));

  return (
    <Dialog isOpen onClose={onClose} title="Export leads" className="li-filedialog">
      <DialogBody>
        <div className="li-stack">
          {sheets.error instanceof DriveNotConnected && (
            <ConnectDriveBanner>Connect Google Drive to open the export in Google Sheets. It is saved to your own Drive. After connecting, choose Open in Google Sheets again.</ConnectDriveBanner>
          )}
          <div className="li-export__filters">
            <div className="li-field">
              <span className="li-field__label">Leads</span>
              <Segmented ariaLabel="Leads to export" options={DUPLICATES} value={filter.duplicates}
                onChange={(d) => set({ duplicates: d, ...(d === 'except' ? { source: null } : {}) })} />
            </div>
            <label className="li-field">
              <span className="li-field__label">Duplicate of</span>
              <PickList value={String(filter.source ?? '')} disabled={filter.duplicates === 'except'} ariaLabel="Duplicate of" fill
                onChange={(v) => set({ source: v === '' ? null : Number(v) })}
                options={[{ value: '', label: 'Any source' }, ...sources.map((s) => ({ value: String(s.id), label: s.label }))]} />
            </label>
            <label className="li-field">
              <span className="li-field__label">Rep</span>
              <PickList value={String(filter.rep ?? '')} ariaLabel="Rep" fill
                onChange={(v) => set({ rep: v === '' ? null : v === 'none' ? 'none' : Number(v) })}
                options={[{ value: '', label: 'Anyone' }, { value: 'none', label: 'Unassigned' }, ...reps.map((r) => ({ value: String(r.rep), label: r.name }))]} />
            </label>
          </div>
          <p className="li-export__count" data-testid="li-export-count" aria-live="polite">
            {preview.isPending ? 'Counting…' : `${fmtInt(total)} lead${total === 1 ? '' : 's'}`}
            {total > PREVIEW_ROWS && <span className="li-muted">, first {PREVIEW_ROWS} shown</span>}
          </p>
          {preview.error ? <ErrorNote error={preview.error} /> : (
            <div className={`li-tablewrap li-tablewrap--scroll li-export__preview${preview.isPlaceholderData ? ' is-stale' : ''}`}>
              <table className="li-table" aria-label="Export preview">
                <thead><tr>{preview.data?.columns.map((c) => <th key={c}>{c}</th>)}</tr></thead>
                <tbody>
                  {preview.data?.rows.map((r, i) => (
                    <tr key={i}>{r.map((v, j) => <td key={j} className={typeof v === 'number' ? 'r num' : undefined}>{v ?? ''}</td>)}</tr>
                  ))}
                </tbody>
              </table>
              {preview.data && total === 0 && <p className="li-muted li-export__none">No leads match. Change the filters above.</p>}
            </div>
          )}
        </div>
      </DialogBody>
      <DialogFooter actions={<>
        <button type="button" className="li-btn li-btn--quiet" onClick={onClose}>Close</button>
        <button type="button" className="li-btn li-btn--secondary" disabled={sheets.isPending || total === 0}
          onClick={() => sheets.mutate(window.open('', '_blank'))}>
          {sheets.isPending ? 'Copying to Drive…' : 'Open in Google Sheets'}
        </button>
        <button type="button" className="li-btn li-btn--primary" disabled={download.isPending || total === 0} onClick={() => download.mutate()}>
          {download.isPending ? 'Preparing…' : `Download CSV (${fmtInt(total)})`}
        </button>
      </>} />
    </Dialog>
  );
}
